import { resolveLatestCharacterSpriteData } from './sprite-state.js';
import { applyCharacterRemovalsFromSystemMessages } from './session-reconstruct.js';

export const buildCharacterVisualMap = (originalConfig, conversationMetadata) => {
  const visualCharacterMap = new Map();
  const sourceCollections = [
    originalConfig?.allCharacters,
    conversationMetadata?.allCharacters
  ];

  sourceCollections.forEach((characters) => {
    if (!Array.isArray(characters)) return;
    characters.forEach((char) => {
      const normalizedId = String(char?.id ?? char?.characterId ?? '');
      if (!normalizedId) return;
      const existingCharacter = visualCharacterMap.get(normalizedId) || {};
      visualCharacterMap.set(normalizedId, {
        ...existingCharacter,
        ...char
      });
    });
  });

  return visualCharacterMap;
};

export const mergeCharacterVisualData = (character, visualCharacterMap) => {
  const normalizedId = String(character?.id ?? character?.characterId ?? '');
  if (!normalizedId) return character;
  const visualCharacter = visualCharacterMap.get(normalizedId);
  if (!visualCharacter) return character;
  return {
    ...visualCharacter,
    ...character,
    id: character?.id ?? visualCharacter?.id,
    name: character?.name ?? visualCharacter?.name
  };
};

export const extractCharactersFromMessages = (
  characterId,
  conversationMetadata,
  lastMessage,
  visualCharacterMap = new Map(),
  messages = []
) => {
  const primaryVisualCharacter = visualCharacterMap.get(String(characterId)) || {};
  const characterIds = new Set([String(characterId)]);
  const characterData = {
    [String(characterId)]: {
      ...primaryVisualCharacter,
      id: characterId,
      name: conversationMetadata?.characterName || lastMessage?.characterName || primaryVisualCharacter?.name || 'Character',
      outfitId: conversationMetadata?.outfitId || lastMessage?.outfitId || primaryVisualCharacter?.outfitId || 0
    }
  };

  (Array.isArray(messages) ? messages : []).forEach((msg) => {
    if (msg.role === 'assistant' && msg.characterId && !characterIds.has(String(msg.characterId))) {
      characterIds.add(String(msg.characterId));
      const visualCharacter = visualCharacterMap.get(String(msg.characterId)) || {};
      characterData[String(msg.characterId)] = {
        ...visualCharacter,
        id: msg.characterId,
        name: msg.characterName || visualCharacter?.name || `Character ${msg.characterId}`,
        outfitId: msg.outfitId || visualCharacter?.outfitId || 0
      };
    }
  });

  return characterData;
};

export const extractCharacterData = ({
  sessionStateFromDB,
  originalConfig,
  conversationMetadata,
  characterId,
  lastMessage,
  messages = []
}) => {
  const visualCharacterMap = buildCharacterVisualMap(originalConfig, conversationMetadata);

  if (sessionStateFromDB?.hasStateData && sessionStateFromDB.characters.length > 0) {
    const locId = sessionStateFromDB.location != null && !originalConfig.isNewConversation
      ? String(sessionStateFromDB.location)
      : null;
    return {
      characters: sessionStateFromDB.characters
        .map((char) => mergeCharacterVisualData(char, visualCharacterMap))
        .filter((char) => char?.id),
      locId
    };
  }

  if (originalConfig.allCharacters?.length > 0) {
    return {
      characters: originalConfig.allCharacters
        .map((char) => mergeCharacterVisualData(char, visualCharacterMap))
        .filter((char) => char?.id),
      locId: null
    };
  }

  if (conversationMetadata?.allCharacters?.length > 0) {
    return {
      characters: conversationMetadata.allCharacters
        .map((char) => mergeCharacterVisualData(char, visualCharacterMap))
        .filter((char) => char?.id),
      locId: null
    };
  }

  const characterData = extractCharactersFromMessages(
    characterId,
    conversationMetadata,
    lastMessage,
    visualCharacterMap,
    messages
  );
  return {
    characters: Object.values(characterData).filter((char) => char?.id),
    locId: null
  };
};

export const hydrateCharactersFromMessages = (allCharacters, messages = [], config = null) => {
  if (!Array.isArray(allCharacters) || allCharacters.length === 0) {
    return { characters: allCharacters, didChange: false };
  }

  let didChange = false;
  const characters = allCharacters.map((character) => {
    const characterId = String(character?.id ?? '');
    if (!characterId) return character;
    const spriteData = resolveLatestCharacterSpriteData({
      messages,
      characterId,
      config
    });
    let actionName = null;
    for (let index = messages.length - 1; index >= 0; index -= 1) {
      const message = messages[index];
      if (message?.role !== 'assistant') continue;
      if (String(message.characterId ?? message.character_id ?? '') !== characterId) continue;
      const name = typeof message.actionName === 'string' ? message.actionName.trim() : '';
      if (!name && (message.actionId === null || message.actionId === undefined) && spriteData.actionId == null) {
        continue;
      }
      actionName = name || null;
      break;
    }
    if (spriteData.actionId == null && !actionName) return character;
    if (
      (spriteData.actionId == null || spriteData.actionId === character.actionId)
      && (!actionName || actionName === character.actionName)
    ) {
      return character;
    }
    didChange = true;
    return {
      ...character,
      ...(spriteData.actionId != null ? { actionId: spriteData.actionId } : {}),
      ...(actionName ? {
        actionName,
        spriteState: { ...(character.spriteState || {}), action: actionName }
      } : {})
    };
  });

  return { characters, didChange };
};

export const reconstructActiveCharactersFromMessages = async (messages, { enrichCharacters } = {}) => {
  const characterMap = new Map();
  (messages || []).forEach((msg, index) => {
    if (msg.role !== 'assistant' || !msg.characterId) return;
    const charId = String(msg.characterId);
    if (!characterMap.has(charId)) {
      characterMap.set(charId, {
        id: msg.characterId,
        name: msg.characterName || `Character ${msg.characterId}`,
        outfitId: msg.outfitId && msg.outfitId !== 0 ? msg.outfitId : 0,
        actionId: msg.actionId ?? null,
        goal: msg.goal || null,
        firstMessageIndex: index,
        lastMessageIndex: index
      });
      return;
    }
    const char = characterMap.get(charId);
    char.name = msg.characterName || char.name;
    if (msg.outfitId && msg.outfitId !== 0) char.outfitId = msg.outfitId;
    char.actionId = msg.actionId ?? char.actionId;
    char.goal = msg.goal || char.goal;
    char.lastMessageIndex = index;
  });

  applyCharacterRemovalsFromSystemMessages(characterMap, messages);

  if (typeof enrichCharacters === 'function') {
    await enrichCharacters(characterMap);
  }

  return Array.from(characterMap.values()).sort((a, b) => a.firstMessageIndex - b.firstMessageIndex);
};
